import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { buildFileUrl } from '../utils/fileUrl';
import ProductionContactModal from './ProductionContactModal';
import {
  Search, Package, Hammer, Clock, User,
  CheckCircle, Circle, Eye, Phone, Wrench,
  Camera, Play, Check, ShieldCheck, Box, RefreshCw, FileText, X, Loader2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const STAGE_CONFIG = {
  PRODUCTION: { label: 'Produção em Fábrica', icon: Hammer, color: 'blue', bg: 'bg-blue-50', border: 'border-blue-200' },
  ASSEMBLY: { label: 'Montagem no Cliente', icon: Wrench, color: 'orange', bg: 'bg-orange-50', border: 'border-orange-200' }
};

const ProductionDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactProject, setContactProject] = useState(null);
  const [photoStepName, setPhotoStepName] = useState('Pré-Montagem na Fábrica');
  const [photoNotes, setPhotoNotes] = useState('');

  // Estados do Modal de Aceite Digital com Assinatura do Cliente
  const [showAcceptanceModal, setShowAcceptanceModal] = useState(false);
  const [acceptanceProject, setAcceptanceProject] = useState(null);
  const [clientSignName, setClientSignName] = useState('');
  const [signedPdfUrl, setSignedPdfUrl] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const canvasRef = React.useRef(null);
  const [checklistChecks, setChecklistChecks] = useState({
    portas: true,
    gavetas: true,
    caixaria: true,
    limpeza: true,
    garantia: true
  });

  // Buscar ordens de produção ativas no chão de fábrica (Totem do Marceneiro)
  const { data: activeOrdersResponse, isLoading: loadingOrders, refetch } = useQuery({
    queryKey: ['active-production-orders', searchTerm],
    queryFn: async () => {
      const res = await api.get('/production-dashboard/active-orders', {
        params: { search: searchTerm }
      });
      return res.data;
    },
    staleTime: 10000
  });

  const activeOrders = activeOrdersResponse?.data || [];

  // Mutation para atualizar etapa na bancada
  const updateStepMutation = useMutation({
    mutationFn: async ({ projectId, step_name, status, notes }) => {
      const res = await api.post(`/production-dashboard/projects/${projectId}/update-step-status`, {
        step_name, status, notes
      });
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Etapa atualizada com sucesso!');
      queryClient.invalidateQueries(['active-production-orders']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao atualizar etapa');
    }
  });

  // Mutation para checar e reservar insumos
  const reserveMaterialsMutation = useMutation({
    mutationFn: async (projectId) => {
      const res = await api.post(`/production-dashboard/projects/${projectId}/reserve-materials`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Insumos checados e reservados no almoxarifado!');
      queryClient.invalidateQueries(['active-production-orders']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao checar estoque');
    }
  });

  // Upload de Foto de Inspeção da Bancada
  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ projectId, step_name, photo_base64, notes }) => {
      const res = await api.post(`/production-dashboard/projects/${projectId}/upload-photo`, {
        step_name, photo_base64, notes
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success('📷 Foto de inspeção enviada com sucesso!');
      setShowPhotoModal(false);
      queryClient.invalidateQueries(['active-production-orders']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao enviar foto');
    }
  });

  // Mutation para envio de Aceite Digital do Cliente com Assinatura
  const submitAcceptanceMutation = useMutation({
    mutationFn: async ({ projectId, payload }) => {
      const res = await api.post(`/production-dashboard/projects/${projectId}/submit-client-acceptance`, payload);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success('🎉 Obra entregue e Termo de Aceite assinado com sucesso!');
      setSignedPdfUrl(data.data?.pdf_url || null);
      queryClient.invalidateQueries(['active-production-orders']);
      queryClient.invalidateQueries(['projects-by-stage']);
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || 'Erro ao registrar aceite digital');
    }
  });

  // Canvas de Assinatura Digital
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#1e293b';
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const handleOpenAcceptance = (project) => {
    setAcceptanceProject(project);
    setClientSignName(project.client_name || '');
    setSignedPdfUrl(null);
    setShowAcceptanceModal(true);
  };

  const handleSaveAcceptance = () => {
    if (!canvasRef.current || !acceptanceProject) return;
    const signatureBase64 = canvasRef.current.toDataURL('image/png');

    const checklistData = [
      checklistChecks.portas && 'Portas alinhadas e dobradiças reguladas',
      checklistChecks.gavetas && 'Gavetas com amortecimento testadas',
      checklistChecks.caixaria && 'Caixaria limpa e sem furações aparentes',
      checklistChecks.limpeza && 'Iluminação LED testada e funcionando',
      checklistChecks.garantia && 'Cliente instruído sobre cuidados e garantia'
    ].filter(Boolean);

    submitAcceptanceMutation.mutate({
      projectId: acceptanceProject.id,
      payload: {
        client_name: clientSignName,
        signature_base64: signatureBase64,
        checklist_data: checklistData
      }
    });
  };

  const handleCapturePhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProject) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result;
      uploadPhotoMutation.mutate({
        projectId: selectedProject.id,
        step_name: photoStepName,
        photo_base64: base64,
        notes: photoNotes
      });
    };
    reader.readAsDataURL(file);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const [y, m, d] = date.split('T')[0].split('-');
    return `${d}/${m}/${y}`;
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const renderProjectCard = (project, stage) => {
    const config = STAGE_CONFIG[stage] || STAGE_CONFIG.PRODUCTION;
    const Icon = config.icon;
    const schedule = project.planned_schedule || [];
    const squad = project.team_squad || [];
    const stageLogs = project.stage_logs || [];

    return (
      <div key={project.id} className="bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-400 shadow-md transition-all overflow-hidden">
        {/* Header do Card */}
        <div className="p-4 bg-gray-50/80 border-b border-gray-200 flex items-start justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 bg-blue-100 text-blue-900 font-extrabold rounded-lg text-sm tracking-wide flex items-center gap-1.5">
                <Icon className="w-4 h-4 text-blue-700" />
                {project.project_code || `PROJ-${project.id}`}
              </span>
              <span className="text-xs font-semibold text-gray-500 uppercase">
                Etapa: <strong className="text-gray-800">{project.current_stage || 'PRODUÇÃO'}</strong>
              </span>
              {project.estimated_budget && parseFloat(project.estimated_budget) > 0 && (
                <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  {formatCurrency(project.estimated_budget)}
                </span>
              )}
            </div>
            <h3 className="text-base font-bold text-gray-900 mt-1">
              {project.project_name || project.client_name}
            </h3>
            <p className="text-xs text-gray-600 flex flex-wrap items-center gap-2 mt-0.5">
              <span className="flex items-center gap-1">
                <User className="w-3.5 h-3.5 text-gray-400" />
                {project.client_name}
              </span>
              {project.client_phone && <span>• {project.client_phone}</span>}
              {project.estimated_completion_date && (
                <span className="text-gray-400">
                  • Previsão: <strong className="text-gray-700">{formatDate(project.estimated_completion_date)}</strong>
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => reserveMaterialsMutation.mutate(project.id)}
              disabled={reserveMaterialsMutation.isPending}
              className="p-2 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-xl transition-colors text-xs font-bold flex items-center gap-1 border border-emerald-200 shadow-2xs"
              title="Checar e Reservar Insumos no Almoxarifado"
            >
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Insumos</span>
            </button>
            <button
              onClick={() => handleOpenAcceptance(project)}
              className="p-2 bg-purple-50 text-purple-700 hover:bg-purple-100 rounded-xl transition-colors text-xs font-bold flex items-center gap-1 border border-purple-200 shadow-2xs"
              title="Coletar Assinatura Digital do Cliente & Termo de Entrega"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Termo Aceite</span>
            </button>
          </div>
        </div>

        {/* Squad / Equipe Alocada */}
        {squad.length > 0 && (
          <div className="px-4 py-2 bg-purple-50/40 border-b border-purple-100 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-bold text-purple-900 flex items-center gap-1">
              <User className="w-3.5 h-3.5 text-purple-600" /> Squad:
            </span>
            {squad.map((member, i) => (
              <span key={i} className="px-2 py-0.5 bg-white text-gray-800 rounded-md border border-purple-200 font-medium">
                {member.user_name} ({member.role === 'MARCENEIRO_LIDER' ? 'Marceneiro Líder' : member.role})
              </span>
            ))}
          </div>
        )}

        {/* 7 Macroetapas Sequenciais - Painel de Bancada Touch */}
        <div className="p-4 space-y-2.5">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wider flex items-center justify-between">
            <span>Macroetapas da Bancada ({schedule.length > 0 ? schedule.length : 7})</span>
            <span className="text-[11px] text-gray-400 font-normal">Toque para atualizar</span>
          </p>

          <div className="space-y-2">
            {(schedule.length > 0 ? schedule : [
              { step_name: '1. Medição Fina', status: 'COMPLETED' },
              { step_name: '2. Detalhamento & Projetos', status: 'COMPLETED' },
              { step_name: '3. Compras & Insumos', status: 'COMPLETED' },
              { step_name: '4. Corte & Usinagem', status: 'IN_PROGRESS' },
              { step_name: '5. Borda & Usinagem', status: 'PENDING' },
              { step_name: '6. Pré-Montagem na Fábrica', status: 'PENDING' },
              { step_name: '7. Montagem na Obra', status: 'PENDING' }
            ]).map((step, idx) => {
              const isCompleted = step.status === 'COMPLETED';
              const isInProgress = step.status === 'IN_PROGRESS';

              return (
                <div
                  key={idx}
                  className={`p-2.5 rounded-xl border flex items-center justify-between transition-all ${
                    isCompleted ? 'bg-green-50/70 border-green-200 text-green-900' :
                    isInProgress ? 'bg-blue-50/90 border-blue-300 text-blue-900 ring-2 ring-blue-400/20' :
                    'bg-gray-50 border-gray-200 text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4 text-green-600 shrink-0" />
                    ) : isInProgress ? (
                      <Clock className="w-4 h-4 text-blue-600 animate-spin shrink-0" />
                    ) : (
                      <Circle className="w-4 h-4 text-gray-400 shrink-0" />
                    )}
                    <span className="text-xs font-bold truncate">{step.step_name}</span>
                  </div>

                  {/* Botões Touch da Bancada */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isInProgress ? (
                      <>
                        <button
                          onClick={() => {
                            setSelectedProject(project);
                            setPhotoStepName(step.step_name);
                            setShowPhotoModal(true);
                          }}
                          className="px-2 py-1 bg-white hover:bg-gray-100 text-gray-700 rounded-lg border border-gray-300 text-[11px] font-bold flex items-center gap-1"
                          title="Tirar/Anexar foto da etapa"
                        >
                          <Camera className="w-3 h-3 text-purple-600" />
                          <span className="hidden sm:inline">Foto</span>
                        </button>
                        <button
                          onClick={() => updateStepMutation.mutate({
                            projectId: project.id,
                            step_name: step.step_name,
                            status: 'COMPLETED'
                          })}
                          disabled={updateStepMutation.isPending}
                          className="px-2.5 py-1 bg-green-600 hover:bg-green-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 shadow-xs"
                        >
                          <Check className="w-3 h-3" />
                          <span>Concluir</span>
                        </button>
                      </>
                    ) : !isCompleted ? (
                      <button
                        onClick={() => updateStepMutation.mutate({
                          projectId: project.id,
                          step_name: step.step_name,
                          status: 'IN_PROGRESS'
                        })}
                        disabled={updateStepMutation.isPending}
                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[11px] font-bold flex items-center gap-1 shadow-xs"
                      >
                        <Play className="w-3 h-3 fill-current" />
                        <span>Iniciar</span>
                      </button>
                    ) : (
                      <span className="text-[10px] font-extrabold uppercase px-2 py-0.5 bg-green-200/60 text-green-800 rounded-md">
                        Feito
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Fotos de Inspeção de Bancada */}
        {stageLogs.some(l => l.photo_url) && (
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
            <p className="text-[11px] font-bold text-gray-600 uppercase mb-1.5 flex items-center gap-1">
              <Camera className="w-3.5 h-3.5 text-purple-600" />
              <span>Fotos de Inspeção ({stageLogs.filter(l => l.photo_url).length})</span>
            </p>
            <div className="flex items-center gap-2 overflow-x-auto pb-1">
              {stageLogs.filter(l => l.photo_url).map((log, i) => (
                <a
                  key={i}
                  href={buildFileUrl(log.photo_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="relative group shrink-0 rounded-lg overflow-hidden border border-gray-300 hover:border-purple-500 transition-all"
                  title={`${log.step_name}: ${log.notes || 'Foto da bancada'}`}
                >
                  <img
                    src={buildFileUrl(log.photo_url)}
                    alt={log.step_name}
                    className="w-12 h-12 object-cover"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                    <Eye className="w-3.5 h-3.5 text-white" />
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Footer com Ações */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between gap-2">
          <button
            onClick={() => navigate(`/projetos/${project.id}/execucao`)}
            className="text-xs font-bold text-blue-600 hover:text-blue-800 flex items-center gap-1"
          >
            <Eye className="w-3.5 h-3.5" />
            <span>Ficha do Projeto</span>
          </button>
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setContactProject(project);
                setShowContactModal(true);
              }}
              className="p-1.5 text-gray-600 hover:bg-gray-200 rounded-lg text-xs"
              title="Contatos do Cliente"
            >
              <Phone className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleOpenAcceptance(project)}
              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg text-xs font-bold shadow-xs hover:from-purple-700 hover:to-indigo-700 flex items-center gap-1"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Aceite Final</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const filteredOrders = activeOrders.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (p.project_name && p.project_name.toLowerCase().includes(term)) ||
      (p.client_name && p.client_name.toLowerCase().includes(term)) ||
      (p.project_code && p.project_code.toLowerCase().includes(term))
    );
  });

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-gray-200 shadow-xs">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-700 flex items-center justify-center font-bold shadow-xs">
            <Hammer className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              Painel de Bancada da Fábrica (Totem)
            </h1>
            <p className="text-xs sm:text-sm text-gray-500">
              Controle em tempo real de corte, pré-montagem, fotos de qualidade e aceite digital da montagem
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="px-4 py-2 bg-white border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 flex items-center gap-2 shadow-2xs"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Atualizar</span>
          </button>
        </div>
      </div>

      {/* Barra de Busca & Filtros */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-xs">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por código PROJ-2026, cliente ou ambiente..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Grid de Ordens de Produção */}
      {loadingOrders ? (
        <div className="p-12 text-center text-gray-500 flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          <p>Carregando ordens de produção da oficina...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="p-12 text-center text-gray-500 bg-white rounded-2xl border border-gray-200">
          <Box className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-base font-semibold text-gray-700">Nenhum projeto em produção na fábrica no momento</p>
          <p className="text-xs text-gray-500 mt-1">
            Projetos liberados na esteira da Carteira (PCP) aparecerão aqui imediatamente para corte e montagem.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOrders.map(project => renderProjectCard(project, project.current_stage || 'PRODUCTION'))}
        </div>
      )}

      {/* Modal 1: Upload de Foto de Bancada / Pré-Montagem */}
      {showPhotoModal && selectedProject && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4 border border-gray-100">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Camera className="w-5 h-5 text-purple-600" />
                <h3 className="font-bold text-gray-900">Foto de Inspeção — {photoStepName}</h3>
              </div>
              <button onClick={() => setShowPhotoModal(false)} className="p-1.5 text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Tirar Foto ou Escolher Arquivo</label>
              <input
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCapturePhoto}
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Notas de Inspeção (Opcional)</label>
              <input
                type="text"
                value={photoNotes}
                onChange={(e) => setPhotoNotes(e.target.value)}
                placeholder="Ex: caixaria 100% montada e gavetas alinhadas..."
                className="w-full px-3 py-2 border border-gray-300 rounded-xl text-sm"
              />
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowPhotoModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Assinatura Digital do Cliente & Termo de Entrega PDF */}
      {showAcceptanceModal && acceptanceProject && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full p-6 space-y-5 border border-gray-100 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-green-600" />
                <div>
                  <h3 className="font-bold text-gray-900 text-base">
                    Termo de Entrega & Aceite Digital — {acceptanceProject.project_code}
                  </h3>
                  <p className="text-xs text-gray-500">Colha a assinatura do cliente na tela do celular/tablet</p>
                </div>
              </div>
              <button onClick={() => setShowAcceptanceModal(false)} className="p-1.5 text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>

            {signedPdfUrl ? (
              <div className="p-6 bg-green-50 border border-green-200 rounded-2xl text-center space-y-3">
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto" />
                <h4 className="text-lg font-bold text-green-900">Obra Finalizada com Sucesso!</h4>
                <p className="text-xs text-green-700">
                  O Termo de Aceite Oficial foi assinado e gerado em PDF com validade digital.
                </p>
                <div className="pt-2 flex flex-wrap items-center justify-center gap-2.5">
                  <a
                    href={buildFileUrl(signedPdfUrl)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl text-sm font-bold shadow-md transition-colors"
                  >
                    <FileText className="w-4 h-4" />
                    <span>Baixar Termo (PDF)</span>
                  </a>

                  {acceptanceProject.client_phone && (
                    <a
                      href={`https://wa.me/55${acceptanceProject.client_phone.replace(/\D/g, '')}?text=${encodeURIComponent(
                        `Olá, ${acceptanceProject.client_name}! Agradecemos pela confiança na Marcenaria Santiago. Segue o seu Termo de Entrega de Obra assinado: ${window.location.origin}${buildFileUrl(signedPdfUrl)}. Qualquer dúvida, estamos à disposição!`
                      )}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] hover:bg-[#1ebd59] text-white rounded-xl text-sm font-bold shadow-md transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      <span>Enviar via WhatsApp</span>
                    </a>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Nome do Cliente */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Nome do Recebedor / Cliente *</label>
                  <input
                    type="text"
                    value={clientSignName}
                    onChange={(e) => setClientSignName(e.target.value)}
                    className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-sm font-semibold text-gray-900"
                    placeholder="Nome completo do cliente..."
                    required
                  />
                </div>

                {/* Checklist de Entrega */}
                <div className="space-y-2 bg-gray-50 p-3.5 rounded-xl border border-gray-200 text-xs">
                  <p className="font-bold text-gray-700 uppercase tracking-wider mb-1">Checklist de Vistoria da Obra:</p>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checklistChecks.portas}
                      onChange={(e) => setChecklistChecks(p => ({ ...p, portas: e.target.checked }))}
                      className="rounded text-green-600"
                    />
                    <span>Portas alinhadas e dobradiças reguladas</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checklistChecks.gavetas}
                      onChange={(e) => setChecklistChecks(p => ({ ...p, gavetas: e.target.checked }))}
                      className="rounded text-green-600"
                    />
                    <span>Gavetas com amortecimento testadas e funcionando</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checklistChecks.caixaria}
                      onChange={(e) => setChecklistChecks(p => ({ ...p, caixaria: e.target.checked }))}
                      className="rounded text-green-600"
                    />
                    <span>Caixaria limpa, sem lascas ou furações aparentes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checklistChecks.limpeza}
                      onChange={(e) => setChecklistChecks(p => ({ ...p, limpeza: e.target.checked }))}
                      className="rounded text-green-600"
                    />
                    <span>Iluminação LED testada e ambiente limpo</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={checklistChecks.garantia}
                      onChange={(e) => setChecklistChecks(p => ({ ...p, garantia: e.target.checked }))}
                      className="rounded text-green-600"
                    />
                    <span>Cliente instruído sobre cuidados de conservação e garantia</span>
                  </label>
                </div>

                {/* Canvas de Assinatura */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-xs font-bold text-gray-700 uppercase">Assinatura Digital do Cliente</label>
                    <button
                      type="button"
                      onClick={clearSignature}
                      className="text-xs text-red-600 font-bold hover:underline"
                    >
                      Limpar Desenho
                    </button>
                  </div>
                  <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white shadow-inner">
                    <canvas
                      ref={canvasRef}
                      width={480}
                      height={150}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                      className="w-full h-36 touch-none cursor-crosshair"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1 text-center">
                    Desenhe a assinatura com o dedo (no celular/tablet) ou mouse (no computador)
                  </p>
                </div>

                {/* Botões do Modal */}
                <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowAcceptanceModal(false)}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-semibold hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveAcceptance}
                    disabled={submitAcceptanceMutation.isPending}
                    className="px-5 py-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white rounded-xl text-sm font-bold shadow-md flex items-center gap-2"
                  >
                    <ShieldCheck className="w-4 h-4" />
                    <span>{submitAcceptanceMutation.isPending ? 'Emitindo Termo...' : 'Concluir Obra & Emitir Termo'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Contatos de Produção */}
      {showContactModal && contactProject && (
        <ProductionContactModal
          projectId={contactProject.id}
          projectName={contactProject.project_name || contactProject.project_code}
          clientName={contactProject.client_name}
          onClose={() => {
            setShowContactModal(false);
            setContactProject(null);
          }}
        />
      )}
    </div>
  );
};

export default ProductionDashboard;